import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  input,
  model,
  viewChild,
} from '@angular/core';
import { uniqueId } from '../form/ids';

/**
 * Interruttore acceso/spento (`role="switch"`). Binario di 52×32 px dentro un'area di tocco di
 * almeno 44 px; acceso è giallo con la manopola nera. Dalla tastiera si cambia con Spazio o
 * Invio. L'etichetta si passa con `label` oppure si proietta. Con `inline` l'interruttore sta a
 * sinistra, subito prima dell'etichetta: per legarlo al campo che lo precede.
 */
@Component({
  selector: 'bs-switch',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex' },
  template: `
    <div [class]="rowClass()">
      <div class="flex min-w-0 flex-col gap-0.5">
        <span [id]="labelId" class="text-sm font-medium text-ink dark:text-stone-100">
          {{ label() }}<ng-content />
        </span>
        @if (description(); as text) {
          <span [id]="descriptionId" class="text-sm text-stone-600 dark:text-stone-300">
            {{ text }}
          </span>
        }
      </div>
      <button
        #control
        type="button"
        role="switch"
        class="group inline-flex h-11 w-14 shrink-0 items-center justify-center rounded-full disabled:cursor-not-allowed disabled:opacity-50"
        [attr.aria-checked]="checked()"
        [attr.aria-labelledby]="labelId"
        [attr.aria-describedby]="description() ? descriptionId : null"
        [disabled]="disabled()"
        [attr.data-testid]="testId()"
        (click)="toggle()"
      >
        <span [class]="trackClass()" aria-hidden="true">
          <span [class]="knobClass()"></span>
        </span>
      </button>
    </div>
  `,
})
export class SwitchComponent {
  readonly checked = model(false);
  readonly label = input<string | null>(null);
  readonly description = input<string | null>(null);
  readonly disabled = input(false);
  /** Interruttore a sinistra dell'etichetta invece che all'estremo destro della riga. */
  readonly inline = input(false);
  readonly testId = input<string | null>(null);

  protected readonly labelId = uniqueId('bs-switch');
  protected readonly descriptionId = `${this.labelId}-description`;
  private readonly control = viewChild.required<ElementRef<HTMLButtonElement>>('control');

  protected readonly rowClass = computed(() =>
    this.inline()
      ? 'flex min-h-11 w-full flex-row-reverse items-center justify-end gap-2'
      : 'flex min-h-11 w-full items-center justify-between gap-4',
  );
  protected readonly trackClass = computed(
    () =>
      `inline-flex h-8 w-13 items-center rounded-full p-1 transition-colors ${
        this.checked()
          ? 'bg-bsite-400'
          : 'bg-stone-300 group-hover:bg-stone-400 dark:bg-stone-600 dark:group-hover:bg-stone-500'
      }`,
  );
  protected readonly knobClass = computed(
    () =>
      `size-6 rounded-full shadow-sm transition-transform ${
        this.checked() ? 'translate-x-5 bg-ink' : 'translate-x-0 bg-white dark:bg-stone-50'
      }`,
  );

  focus(): void {
    this.control().nativeElement.focus();
  }

  protected toggle(): void {
    if (!this.disabled()) {
      this.checked.set(!this.checked());
    }
  }
}
